import type { RiskAttribute } from '@/services/request.service.ts'
import type { Pagination } from '@/types/shared.types'
import { axios } from '@/lib'
import { buildQueryParams, formatSearchTerm } from '@/lib/api'

export interface Format {
  Definition: string
  Name: string
  NameAttribute: string
  TitleAttribute: null
  ListDisplay: ListDisplay[]
  GridDisplay: GridDisplay[]
  AttributeNames: { [key: string]: string }
}

export interface ProjectedRecord {
  Attributes: { [key: string]: string | boolean | undefined }
  Changes: Change[]
  Created: string
  DN: string
  Definition: string
  Deleted: string | null
  Description: string | null
  Details: Detail
  Email: string
  Hash: string
  Id: string
  JoinValues: string[]
  Links: string[] | null
  Modified: string | null
  MultiNames: MultiName
  ObjectType: string
  RiskAttributes: Record<string, RiskAttribute>
  Silo: string
  Type: string
}

export interface ProjectedReference {
  Attribute: string
  Count: number
  Skip: number
  Limit: number
  Term: string
  Results: Record<string, ProjectedRecord[]>
  Scopes: string[]
  Trust: string
}

enum Slot {
  Before = 'Before',
  After = 'After',
  Replace = 'Replace',
}

export interface Detail {
  [key: string]: {
    [key in Slot]: {
      Label: string | null
      ViewModes: string[]
      Url: string | null
      UrlTarget: string | null
      Slot: string
      Icon: string | null
      IconColor: string | null
    }
  }
}

export interface EntityDescriptor {
  Kind: number
  Value: string
}

export interface Change {
  TimeStamp: string
  Links: string[] | null
  ChangeSource: string
  ModificationType: number
  DN: EntityDescriptor
  ObjectType: EntityDescriptor
  Attributes: Attribute
}

export interface Attribute {
  [key: string]: EntityDescriptor
}

export interface DisplayAttribute {
  Attribute: string
  DisplayName: string
  Size: number
  ShowIfNull: boolean
}

export interface MultiName {
  [key: string]: boolean
}

export interface GridDisplay {
  SectionName: null | string
  Columns: boolean
  Size: number
  CardView: boolean
  DetailView: boolean
  ShortView: boolean
  Attributes: DisplayAttribute[]
  Icon: string | null
  Members: boolean
  Entitlements: true
  Collapsed: boolean
  Collapsible: boolean
}

export interface ListDisplay {
  Attribute: string
  DisplayName: string
}

export interface GetProjectedRecordsOptions {
  limit: number
  skip: number
  term?: string
  identities?: string
  resources?: string
}

export interface ProjectedIndexRequest extends Partial<Pagination> {
  silo?: string
  isaccount?: boolean
  rule?: string
}

interface ProjectedIndexResponse {
  Count: number
  Formats: { [key: string]: Format }
  Results: ProjectedRecord[]
}

interface ProjectedShowResponse {
  Record: ProjectedRecord
  Formats: Record<string, Format>
}

interface ProjectedShowReferenceRequest extends Partial<Pagination> {
  projectedRecordId: string
  attribute: string
}

interface ProjectedShowReferenceResponse {
  Reference: ProjectedReference
}

export const projected = {
  async index(input: ProjectedIndexRequest) {
    try {
      // Initialize the parameters for the GET request
      const params = buildQueryParams({
        ...input,
        term: formatSearchTerm(input.term),
      })

      // Perform the GET request to fetch the ProjectedIndexResponse
      const response = await axios.get<ProjectedIndexResponse>('/api/projected', { params })

      return response.data
    } catch (error) {
      // Log the error to the console with a descriptive message
      console.error(`Failed to fetch projected records: ${error}`)
      // Rethrow the error to be handled by the caller
      throw error
    }
  },

  async count(term: string = '') {
    try {
      const params = buildQueryParams({
        term,
      })

      const response = await axios.get<{ [key: string]: number }>('/api/projected/counts', {
        params,
      })

      return response.data
    } catch (error) {
      console.error(`Failed to fetch projected record counts: ${error}`)
      throw error
    }
  },

  async show(id: string = 'self') {
    const response = await axios.get<ProjectedShowResponse>(`/api/projected/${id}`)
    return response.data
  },

  async showReference(input: ProjectedShowReferenceRequest) {
    try {
      // Destructure input for clarity
      const { projectedRecordId, attribute, ...pagination } = input

      // Build params object
      const params = buildQueryParams({
        ...pagination,
        term: formatSearchTerm(pagination.term),
      })

      // Build request URL
      const url = `/api/projected/${projectedRecordId}/${attribute}`

      // Make request and destructure response
      const { data } = await axios.get<ProjectedShowReferenceResponse>(url, {
        params,
      })

      // Return reference from response
      return data.Reference
    } catch (error) {
      console.error(`Failed to fetch projected reference: ${error}`)
      throw error
    }
  },
}

export const projectedReference = {
  async store({
    projectedRecordId,
    referenceName,
    referenceItems,
  }: {
    projectedRecordId: string
    referenceName: string
    referenceItems: string[]
  }) {
    try {
      const response = await axios.post(`/api/projected/${projectedRecordId}/${referenceName}`, {
        Data: referenceItems,
      })
      return response.data
    } catch (error) {
      console.error(`Failed to store projected reference: ${error}`)
      throw error
    }
  },

  async destroy({
    projectedRecordId,
    referenceName,
    referenceItems,
  }: {
    projectedRecordId: string
    referenceName: string
    referenceItems: string[]
  }) {
    try {
      const response = await axios.delete(`/api/projected/${projectedRecordId}/${referenceName}`, {
        data: {
          Data: referenceItems,
        },
      })
      return response.data
    } catch (error) {
      console.error(`Failed to store projected reference: ${error}`)
      throw error
    }
  },
}
